import { format, getHours, isValid, parse, setHours, startOfHour } from 'date-fns';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { NONE } from 'constants/Global';
import { ENTITY_ORIGIN } from 'modules/app/constants';
import type { MayBe } from 'modules/global/typing/common/common';
import type { Label } from 'modules/global/typing/common/Label';
import { getLabelTranslation } from 'utils/global';

/**
 * Identity `format`, to opt out of react-final-form's `defaultFormat`, which coerces an
 * `undefined` value to `''`. Use it on any field whose empty state must stay `undefined`
 * (a Select with no selection, a nullable enum, …) rather than an empty string.
 *
 * Generic so `useField<T>` keeps propagating `T` into `input.value` — react-final-form infers its
 * `InputValue` from this return type, so a call site that omits its `useField<T>` ends up `unknown`.
 */
export const formatUndefined = <T, >(value: T): T => value;

export const formatBoolean = (value: unknown): boolean => Boolean(value);

export const formatToArray = <T, >(value: MayBe<T[]>): T[] => value ?? [];

export const parseEmptyArray = <T, >(value: MayBe<T[]>): MayBe<T[]> =>
  (Array.isArray(value) && value.length === 0 ? undefined : value);

/** Maps the `NONE` Select sentinel back to `undefined`. Generic for the same reason as {@link formatUndefined}. */
export const parseNone = <T, >(value: MayBe<T | typeof NONE>): MayBe<T> =>
  (value === NONE ? undefined : (value as MayBe<T>));

/** Maps an empty field to the `NONE` Select sentinel, so an option can match it. */
export const formatNone = <T, >(value: MayBe<T>): T | typeof NONE => (value === undefined ? NONE : value);

export const noneParseFormatter = {
  format: formatNone,
  parse: parseNone
};

export const SUBSCRIPTION = {
  NONE: { subscription: {} },
  LENGTH: { subscription: { length: true } },
  PRISTINE: { subscription: { pristine: true } },
  PRISTINE_VALUE: { subscription: { pristine: true, value: true } },
  VALUE_ONLY: { subscription: { value: true } },
  ERROR_ONLY: { subscription: { error: true } },
  VALUE_ERROR: { subscription: { value: true, error: true } },
  DIRTY_ONLY: { subscription: { dirty: true } },
  CHECKED: { type: 'checkbox', subscription: { value: true } },
  INITIAL_VALUES: { subscription: { value: true, initial: true } },
  ERROR: { subscription: { error: true, value: true, touched: true, focus: true, blur: true } },
  CHECKBOX_VALUE_ONLY: { subscription: { value: true }, type: 'checkbox' },
  NULLABLE_ERROR: { subscription: { value: true }, format: formatUndefined },
  NULLABLE_VALUE_ONLY: { subscription: { value: true }, format: formatUndefined },
  BOOLEAN_VALUE_ONLY: { subscription: { value: true }, format: formatBoolean },
  VALUE_AS_ARRAY: { subscription: { value: true }, format: formatToArray },
  VALIDATING: { subscription: { validating: true } },
  VALIDATION: { subscription: { hasValidationErrors: true, pristine: true } },
  MANUAL_VALIDATION: { subscription: { hasValidationErrors: true, values: true, initialValues: true } },

  FORM_VALUES: { subscription: { values: true } },
  FORM_ERRORS: { subscription: { errors: true } },
  FORM_INITIAL_VALUES: { subscription: { initialValues: true } }
};

export type OptionGroupName = 'file' | 'dealer' | 'group' | 'default';

export type DataSourceOption = {
  id?: string;
  name?: ReactNode;
  dealerId?: string;
  groupId?: string;
  selfServiceId?: string;
  origin?: string;
};

type OptionGroup<T extends DataSourceOption> = {
  label: ReactNode;
  options: (T & { value: MayBe<string>; label: ReactNode })[];
};

// Key order drives the rendered group order in formatDataSource.
const GROUP_ORDER_MAP: Record<OptionGroupName, number> = {
  file: -1,
  dealer: 0,
  group: 1,
  default: 2
};

const getDefaultGroups = <T extends DataSourceOption>(): Record<OptionGroupName, OptionGroup<T>> =>
  (Object.keys(GROUP_ORDER_MAP) as OptionGroupName[]).reduce(
    (acc, group) => ({
      ...acc,
      [group]: {
        label: <FormattedMessage id={group} defaultMessage={group} />,
        options: []
      }
    }),
    {} as Record<OptionGroupName, OptionGroup<T>>
  );

export const getOptionGroup = ({ dealerId, groupId, origin, selfServiceId }: DataSourceOption): OptionGroupName => {
  if ((dealerId && groupId) || origin === ENTITY_ORIGIN.DEALER) {
    return 'dealer';
  }
  if (groupId || origin === ENTITY_ORIGIN.GROUP) {
    return 'group';
  }
  if (selfServiceId) {
    return 'file';
  }
  return 'default';
};

type GetLabelFn<T extends DataSourceOption> = (option: T, section: OptionGroupName, defaultName: MayBe<ReactNode>) => ReactNode;

const defaultGetLabel: GetLabelFn<DataSourceOption> = ({ name }, section, defaultName) =>
  (section === 'default' ? defaultName ?? name : name);

/**
 * Formats a datasource array into grouped select options.
 * Groups options by: file, dealer, group, or default based on their properties.
 *
 * @example
 * formatDataSource([
 *   { id: '1', name: 'Item 1', dealerId: 'D1', groupId: 'G1' },
 *   { id: '2', name: 'Item 2', groupId: 'G1' }
 * ])
 * // Returns: [{ label: 'Dealer', options: [...] }, { label: 'Group', options: [...] }]
 */
export const formatDataSource = <T extends DataSourceOption>(
  dataSource: MayBe<T[]>,
  defaultName?: MayBe<ReactNode>,
  getLabel: GetLabelFn<T> = defaultGetLabel as GetLabelFn<T>
): OptionGroup<T>[] => {
  const formatted =
    dataSource?.reduce((acc, option) => {
      const section = getOptionGroup(option);

      return {
        ...acc,
        [section]: {
          ...acc[section],
          options: [
            ...acc[section].options,
            {
              ...option,
              value: option.id,
              label: getLabel(option, section, defaultName)
            }
          ]
        }
      };
    }, getDefaultGroups<T>()) ?? getDefaultGroups<T>();

  return Object.values(formatted).filter(({ options }) => options.length > 0);
};

/**
 * Creates a path parser function for building field paths.
 * Used with template literals to create nested field names.
 *
 * @example
 * const path = pathParser('config.selfService');
 * const fieldName = path`questionForm.enabled`;
 * // Returns: 'config.selfService.questionForm.enabled'
 */
export const pathParser = (basename: string) => (str: TemplateStringsArray) =>
  (basename ? `${basename}.${str.raw[0]}` : str.raw[0]);

export const getParentPathFromFieldName = (fieldName: string): MayBe<string> => {
  const pathRegex = /(?:\.([^.[]+)|\[(\d+)\])$/;
  const match = pathRegex.exec(fieldName);
  return match ? fieldName.slice(0, match.index) : undefined;
};

export const parseHour = (date: MayBe<Date | number>): MayBe<number> => date && getHours(date);

export const parseTimeString = (date: MayBe<Date | number | string>): MayBe<string> => {
  if (!date) {
    return undefined;
  }
  return format(startOfHour(date), 'HH:mm');
};

export const formatTimeString = (time: MayBe<Date | number | string>): MayBe<Date> => {
  if (!time || typeof time !== 'string') {
    return undefined;
  }
  const parsed = parse(time, 'HH:mm', new Date());
  return isValid(parsed) ? startOfHour(parsed) : undefined;
};

/**
 * Formats an hour (number) into a Date object set to that hour, with minutes and seconds set to zero.
 */
export const formatHour = (hour: MayBe<number>): MayBe<Date> =>
  (typeof hour === 'number' ? startOfHour(setHours(new Date(), hour)) : undefined);

/**
 * Resolves a {@link Label} to the translation to display, through the application's standard
 * fallback. Returns `''` rather than `undefined` for a label carrying no usable text, so the
 * result stays safe to bind to a controlled input.
 */
export const formatLabel = (language: string, dealerLanguage: string) =>
  (value: MayBe<Label>): string => getLabelTranslation(value, language, dealerLanguage) ?? '';

/** Reads a single language out of a {@link Label}, for a field scoped to one language. */
export const formatLabelLanguage = (language: string) =>
  (value: MayBe<Label>): string => value?.[language] ?? '';

/**
 * Writes a single language into a {@link Label}, keeping the other translations. `getStored` is
 * read at change time rather than captured, so the surviving translations are never stale.
 */
export const parseLabelLanguage = (language: string, getStored: () => MayBe<Label>) =>
  (value: string): Label => ({ ...getStored(), [language]: value });
